// src/pages/TranscriptionService.js - Enhanced version with drag & drop, save, copy
import { useState, useEffect, useRef, useCallback } from "react"
import {
  FileAudio, Upload, Download, Clock, Mic,
  CheckCircle, AlertCircle, Loader, Copy, FileText,
  Globe, Save, History, Trash2, Play, X
} from "lucide-react"
import { useLanguage } from "../context/LanguageContext"

const TranscriptionService = () => {
  const { language: uiLang, t } = useLanguage()
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState("")
  const [language, setLanguage] = useState("auto")
  const [usageStats, setUsageStats] = useState(null)
  const [savedTranscriptions, setSavedTranscriptions] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const dropZoneRef = useRef(null)

  useEffect(() => {
    fetchUsageStats()
    loadSavedTranscriptions()
  }, [])

  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/subscription-info", {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setUsageStats(data)
      }
    } catch (err) {
      console.error("Failed to fetch usage stats:", err)
    }
  }

  const loadSavedTranscriptions = () => {
    try {
      const saved = localStorage.getItem("saved_transcriptions")
      if (saved) {
        setSavedTranscriptions(JSON.parse(saved))
      }
    } catch (err) {
      console.error("Failed to load saved transcriptions:", err)
    }
  }

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.target === dropZoneRef.current) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      // Check if it's an audio file
      if (droppedFile.type.startsWith('audio/') || droppedFile.type.startsWith('video/') ||
          /\.(mp3|wav|m4a|mp4|mpeg|mpga|webm|ogg|flac|aac|wma|opus)$/i.test(droppedFile.name)) {
        setFile(droppedFile)
        setError("")
        setResult(null)
      } else {
        setError("Please drop an audio file")
      }
    }
  }, [])

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setError("")
      setResult(null)
    }
  }

  const handleTranscribe = async () => {
    if (!file) {
      setError("Please select a file")
      return
    }

    setLoading(true)
    setError("")

    const formData = new FormData()
    formData.append("file", file)
    formData.append("language", language)

    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/transcribe", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      if (response.status === 402) {
        setError("Subscription required. Please subscribe to use transcription service.")
        return
      }

      if (response.status === 429) {
        setError("Monthly limit reached. Please upgrade your plan.")
        return
      }

      if (!response.ok) {
        throw new Error("Transcription failed")
      }

      const data = await response.json()
      setResult({
        ...data,
        fileName: file.name,
        timestamp: new Date().toISOString()
      })

      fetchUsageStats()
    } catch (err) {
      setError(err.message || "Transcription failed")
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (result) {
      const text = result.text || result.formatted
      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    }
  }

  const saveTranscription = () => {
    if (!result) return

    const transcription = {
      id: Date.now(),
      fileName: result.fileName || file?.name || 'Unknown',
      text: result.text || result.formatted,
      language: result.language,
      wordCount: result.word_count,
      timestamp: result.timestamp || new Date().toISOString()
    }

    const updated = [transcription, ...savedTranscriptions].slice(0, 50) // Keep last 50
    setSavedTranscriptions(updated)
    localStorage.setItem("saved_transcriptions", JSON.stringify(updated))

    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 2000)
  }

  const loadTranscription = (transcription) => {
    setResult({
      text: transcription.text,
      formatted: transcription.text,
      language: transcription.language,
      word_count: transcription.wordCount,
      fileName: transcription.fileName,
      timestamp: transcription.timestamp
    })
    setShowHistory(false)
  }

  const deleteTranscription = (id) => {
    const updated = savedTranscriptions.filter(t => t.id !== id)
    setSavedTranscriptions(updated)
    localStorage.setItem("saved_transcriptions", JSON.stringify(updated))
  }

  const downloadAsText = () => {
    if (!result) return

    const text = result.text || result.formatted
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transcript_${result.language || 'unknown'}_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const formatTranscriptText = (text) => {
    if (!text) return []

    // Split into paragraphs/sentences for better readability
    const sentences = text.split(/(?<=[.!?])\s+/)
    const paragraphs = []
    let currentParagraph = []

    sentences.forEach((sentence, index) => {
      currentParagraph.push(sentence)
      // Create paragraph every 3-4 sentences
      if (currentParagraph.length >= 3 || index === sentences.length - 1) {
        paragraphs.push(currentParagraph.join(' '))
        currentParagraph = []
      }
    })

    return paragraphs
  }

  const clearFile = () => {
    setFile(null)
    setResult(null)
    setError("")
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{uiLang === "es" ? "Servicio de Transcripcion" : "Transcription Service"}</h1>
              <p className="text-gray-600 mt-1">{uiLang === "es" ? "Convierte audio a texto en multiples idiomas" : "Convert audio to text in multiple languages"}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  showHistory ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <History className="h-5 w-5" />
                {uiLang === "es" ? "Historial" : "History"} ({savedTranscriptions.length})
              </button>
              {usageStats && (
                <div className="text-right bg-gray-50 px-4 py-2 rounded-lg">
                  <p className="text-xs text-gray-500">{uiLang === "es" ? "Uso Mensual" : "Monthly Usage"}</p>
                  <p className="text-lg font-bold text-gray-900">
                    {usageStats.used_this_month} / {usageStats.monthly_limit}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* History Panel */}
        {showHistory && (
          <div className="mb-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{uiLang === "es" ? "Transcripciones Guardadas" : "Saved Transcriptions"}</h2>
              <button onClick={() => setShowHistory(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            {savedTranscriptions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{uiLang === "es" ? "No hay transcripciones guardadas" : "No saved transcriptions yet"}</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {savedTranscriptions.map((t) => (
                  <div key={t.id} className="border rounded-lg p-4 hover:border-blue-300 transition">
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-medium text-gray-900 truncate flex-1">{t.fileName}</p>
                      <button
                        onClick={() => deleteTranscription(t.id)}
                        className="text-red-400 hover:text-red-600 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {new Date(t.timestamp).toLocaleDateString()} • {t.wordCount || 0} words
                    </p>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {t.text.substring(0, 100)}...
                    </p>
                    <button
                      onClick={() => loadTranscription(t)}
                      className="text-blue-600 text-sm font-medium hover:underline"
                    >
                      {uiLang === "es" ? "Cargar" : "Load Transcript"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{uiLang === "es" ? "Subir Audio" : "Upload Audio"}</h2>

              {/* Drag & Drop Zone */}
              <div className="mb-6">
                <div
                  ref={dropZoneRef}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50 scale-105'
                      : file
                        ? 'border-green-400 bg-green-50'
                        : 'border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {isDragging ? (
                    <>
                      <Upload className="h-12 w-12 text-blue-500 mx-auto mb-3 animate-bounce" />
                      <p className="text-blue-600 font-medium">{uiLang === "es" ? "Suelta tu archivo aqui!" : "Drop your file here!"}</p>
                    </>
                  ) : file ? (
                    <>
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="font-medium text-green-700">{file.name}</p>
                      <p className="text-sm text-green-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      <button
                        onClick={clearFile}
                        className="mt-3 text-sm text-red-500 hover:underline"
                      >
                        {uiLang === "es" ? "Eliminar archivo" : "Remove file"}
                      </button>
                    </>
                  ) : (
                    <>
                      <FileAudio className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <input
                        type="file"
                        accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mpeg,.mpga,.webm,.ogg,.flac,.aac,.wma,.opus"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <span className="text-blue-600 hover:text-blue-700 font-medium">
                          {uiLang === "es" ? "Seleccionar archivo" : "Choose a file"}
                        </span>
                        <span className="text-gray-500">{uiLang === "es" ? " o arrastra aqui" : " or drag & drop"}</span>
                      </label>
                      <p className="text-xs text-gray-400 mt-2">
                        MP3, WAV, M4A, FLAC, OGG, WEBM (up to 25MB)
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Language Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="inline h-4 w-4 mr-1" />
                  {uiLang === "es" ? "Idioma" : "Language"}
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="auto">{uiLang === "es" ? "Auto-detectar" : "Auto-detect"}</option>
                  <option value="en">English</option>
                  <option value="es">Español (Spanish)</option>
                  <option value="fr">Français (French)</option>
                  <option value="de">Deutsch (German)</option>
                  <option value="it">Italiano (Italian)</option>
                  <option value="pt">Português (Portuguese)</option>
                  <option value="zh">中文 (Chinese)</option>
                  <option value="ja">日本語 (Japanese)</option>
                  <option value="ko">한국어 (Korean)</option>
                  <option value="ar">العربية (Arabic)</option>
                  <option value="hi">हिन्दी (Hindi)</option>
                  <option value="ru">Русский (Russian)</option>
                </select>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Transcribe Button */}
              <button
                onClick={handleTranscribe}
                disabled={loading || !file}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    {uiLang === "es" ? "Transcribiendo..." : "Transcribing..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Mic className="h-5 w-5 mr-2" />
                    {uiLang === "es" ? "Transcribir Audio" : "Transcribe Audio"}
                  </span>
                )}
              </button>

              {/* Features */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">{uiLang === "es" ? "Caracteristicas" : "Features"}</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✓ {uiLang === "es" ? "Arrastrar y soltar" : "Drag & drop upload"}</li>
                  <li>✓ {uiLang === "es" ? "Deteccion automatica de idioma" : "Auto language detection"}</li>
                  <li>✓ {uiLang === "es" ? "Guardar y cargar transcripciones" : "Save & load transcripts"}</li>
                  <li>✓ {uiLang === "es" ? "Copiar y descargar texto" : "Copy & download text"}</li>
                  <li>✓ {uiLang === "es" ? "95%+ de precision" : "95%+ accuracy"}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            {result ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{uiLang === "es" ? "Resultado de Transcripcion" : "Transcription Result"}</h2>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                      {result.word_count && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          {result.word_count} {uiLang === "es" ? "palabras" : "words"}
                        </span>
                      )}
                      {result.language && (
                        <span className="flex items-center gap-1">
                          <Globe className="h-4 w-4" />
                          {result.language === 'en' ? 'English' :
                           result.language === 'es' ? 'Spanish' :
                           result.language.toUpperCase()}
                        </span>
                      )}
                      {result.processing_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {result.processing_time}s
                        </span>
                      )}
                      {result.fileName && (
                        <span className="text-gray-400">
                          {result.fileName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveTranscription}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                        saveSuccess
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Save transcription"
                    >
                      {saveSuccess ? <CheckCircle className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                      {saveSuccess ? 'Saved!' : 'Save'}
                    </button>
                    <button
                      onClick={downloadAsText}
                      className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                      title="Download as text"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </button>
                    <button
                      onClick={copyToClipboard}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                        copied
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                      title="Copy to clipboard"
                    >
                      {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Transcript Display with formatting */}
                <div className="bg-gray-50 rounded-lg p-6 max-h-[600px] overflow-y-auto">
                  <div className="prose prose-sm max-w-none">
                    {formatTranscriptText(result.formatted || result.text).map((paragraph, index) => (
                      <p key={index} className="mb-4 text-gray-700 leading-relaxed select-text">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Raw text view option */}
                <details className="mt-4">
                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                    View raw text
                  </summary>
                  <pre className="mt-2 p-4 bg-gray-100 rounded-lg text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto select-text">
                    {result.text || result.formatted}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <div className="relative">
                  <Mic className="h-20 w-20 text-blue-100 mx-auto mb-4" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-16 w-16 rounded-full bg-blue-50 animate-ping opacity-30"></div>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Ready to Transcribe
                </h2>
                <p className="text-gray-600 mb-6">
                  Drag & drop or select an audio file to get started
                </p>

                {/* Quick tips */}
                <div className="grid md:grid-cols-3 gap-4 mt-8">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-blue-900">Upload</p>
                    <p className="text-xs text-blue-700">Drag & drop or click</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <Play className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-900">Transcribe</p>
                    <p className="text-xs text-green-700">AI converts to text</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <Copy className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-purple-900">Use</p>
                    <p className="text-xs text-purple-700">Copy, save, or download</p>
                  </div>
                </div>

                {/* Supported Languages */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Supported Languages</h3>
                  <div className="flex flex-wrap justify-center gap-2 mt-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">English</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Español</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">Français</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">Deutsch</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">+ 8 more</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TranscriptionService
